import React, { useMemo, useState, forwardRef, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import Table from "@mui/material/Table";
import { useNavigate } from "react-router-dom";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import ColourConstants from "helpers/colourConstants";
import PopupMenu from "components/Elements/PopupMenu";
import { makeStyles } from "tss-react/mui";
import TableStyle from "styles/application/TableStyle";
import useInfiniteScrollSentinel, { scrollTableToTop } from "hooks/useInfiniteScrollSentinel";
import { appPath, servicesPath } from "helpers/routePaths";

// Icon imports
import { ReactComponent as MenuIcon } from "assets/icons/3dot-icon.svg";
import AutoFitContentInScreen from "components/Layouts/AutoFitContentInScreen";
import EMICheckbox from "components/Elements/EMICheckbox";
import { createTableHeaderClickHandler } from "helpers/utils";

// Init styled components
const AT = TableStyle();

// Size constant
const MAX_LOGO_HEIGHT = 47;

const useStyles = makeStyles()((theme) => ({
	tableBody: {
		whiteSpace: "noWrap",
	},

	lastCell: {
		borderBottom: "none",
	},
	table: {
		borderStyle: "solid",
		fontFamily: "Roboto Condensed",
		fontSize: 14,
		overflowX: "auto",
		borderColor: ColourConstants.tableBorder,
		borderWidth: 1,
		borderRadius: 0,
	},
	tableHeadRow: {
		borderBottomColor: ColourConstants.tableBorder,
		borderBottomStyle: "solid",
		borderBottomWidth: 1,
		backgroundColor: ColourConstants.tableBackground,
		fontWeight: "bold",
		borderRightColor: "#979797",
		borderRightStyle: "solid",
		borderRightWidth: "1px",
	},
	selectedTableHeadRow: {
		borderBottomColor: ColourConstants.tableBorder,
		borderBottomStyle: "solid",
		borderBottomWidth: 1,
		backgroundColor: ColourConstants.tableBackgroundSelected,
		fontWeight: "bold",
		color: "#FFFFFF",
	},
	nameRow: {
		width: "200px",
		height: "10px",
		lineHeight: "1rem",
	},
	clientsRow: {
		width: "20%",
	},
	dataCell: {
		height: 40,
	},
	nameLink: {
		color: ColourConstants.activeLink,
	},
	clientLogo: {
		maxHeight: MAX_LOGO_HEIGHT,
	},
	noImage: {
		color: ColourConstants.commonText,
		opacity: "50%",
	},
}));

const ServiceListTable = forwardRef(({
	data,
	columns,
	headers,
	handleSort,
	handleDeleteDialogOpen,
	handleChnageStatus,
	formattedData,
	currentTableSort,
	selectedServices,
	handleSelectService,
	isReadOnly,
	hasNextPage,
	isFetchingNextPage,
	fetchNextPage,
	isFetchNextPageError,
}, ref) => {
	// Init hooks
	const { classes, cx } = useStyles();
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [selectedData, setSelectedData] = useState(null);
	const [anchorEl, setAnchorEl] = useState(null);

	const { sentinelRef } = useInfiniteScrollSentinel({
		hasNextPage,
		isFetchingNextPage,
		fetchNextPage,
		isError: isFetchNextPageError,
	});

	useImperativeHandle(ref, () => ({ gotoTop: () => scrollTableToTop() }));

	const formattedRows = useMemo(
		() => formattedData(data, navigate, t) || [],
		[data, navigate, formattedData, t]
	);

	const handleSortClick = createTableHeaderClickHandler(currentTableSort, handleSort);

	return (
		<>
			<AutoFitContentInScreen containsTable={true}>
				<Table aria-label="Table" className={classes.table}>
					<AT.TableHead className={classes.taskHeader}>
						<TableRow className={classes.tableHead}>
							{headers.map((header, index) => {
								const disableSortingColumns = ["percentageComplete", "percentageOverTime", "minutesOverTime"];
								const isSortingDisabled = disableSortingColumns.includes(columns[index]);

								return (
								<TableCell
									key={header.id}
									onClick={() => {
										if (!isSortingDisabled) {
											handleSortClick(columns[index]);
										}
									}}
									style={{
										width: header?.width || "auto",
										cursor: isSortingDisabled ? "default" : "pointer"
									}}
									className={cx(classes.nameRow, {
										[classes.selectedTableHeadRow]:
											currentTableSort[0] === columns[index] && !isSortingDisabled,
										[classes.tableHeadRow]:
											currentTableSort[0] !== columns[index] || isSortingDisabled,
									})}
								>
									<AT.CellContainer
										className="flex justify-between"
										style={index === 0 ? { marginLeft: "38px" } : {}}
									>
										{header.name}
										{!isSortingDisabled && (
											<div className="arrow">
												<AT.DescArrow
													fill={
														currentTableSort[0] === columns[index] &&
														currentTableSort[1] === "asc"
															? "#D2D2D9"
															: "#F9F9FC"
													}
													className="arrowUp"
												/>
												<AT.DefaultArrow
													fill={
														currentTableSort[0] === columns[index] &&
														currentTableSort[1] === "desc"
															? "#D2D2D9"
															: "#F9F9FC"
													}
													className="arrowDown"
												/>
											</div>
										)}
									</AT.CellContainer>
								</TableCell>
								);
							})}
						</TableRow>
					</AT.TableHead>
					<TableBody className={classes.tableBody}>
						{formattedRows.map((row, index) => (
							<TableRow key={index}>
								{columns?.map((col, i, arr) => (
									<AT.DataCell key={col}>
										<AT.CellContainer key={col}>
											<AT.TableBodyText>
												{col === "workOrder" ? (
													<span
														style={{
															display: "flex",
															flexDirection: "space-around",
															alignItems: "center",
															gap: 10,
														}}
													>
														<EMICheckbox
															state={
																Array.isArray(data) && data[index] &&
																selectedServices.findIndex(
																	(d) => d.id === data[index].id
																) !== -1
															}
															changeHandler={({ target: { checked } }) => {
																if (Array.isArray(data) && data[index]) {
																	handleSelectService(data[index], checked);
																}
															}}
														/>

														{col === "modelName"
															? row[col] + " " + row["model"]
															: row[col]}
													</span>
												) : col === "modelName" ? (
													row[col] + " " + (row["model"] ?? "")
												) : (
													row[col]
												)}
											</AT.TableBodyText>

											{arr.length === i + 1 ? (
												<AT.DotMenu
													onClick={(e) => {
														setAnchorEl(
															anchorEl === e.currentTarget
																? null
																: e.currentTarget
														);
														setSelectedData(
															anchorEl === e.currentTarget ? null : index
														);
													}}
												>
													<AT.TableMenuButton>
														<MenuIcon />
													</AT.TableMenuButton>

													<PopupMenu
														index={index}
														selectedData={selectedData}
														anchorEl={anchorEl}
														isLast={index === data.length - 1}
														id={row.id}
														clickAwayHandler={() => {
															setAnchorEl(null);
															setSelectedData(null);
														}}
														menuData={[
															{
																key: isReadOnly ? "View" : "Edit", name: isReadOnly ? t("action.view") : t("action.edit"),
																handler: () => {
																	navigate(
																		`${appPath}${servicesPath}/${row.id}`
																	);
																},
																isDelete: false,
																showInStatus: "Edit",
															},
															{
																key: "Cancel", name: t("action.cancel"),
																handler: handleChnageStatus,
																isDelete: false,
																showInStatus: ["S"],
																otherDetail: "X",
															},
															{
																key: "Cancel Checkout", name: t("action.cancelCheckout"),
																handler: handleChnageStatus,
																isDelete: false,
																showInStatus: ["O", "H"],
																otherDetail: "R",
															},
															{
																key: "Reset", name: t("action.reset"),
																handler: handleChnageStatus,
																isDelete: false,
																showInStatus: ["I", "H"],
																otherDetail: "R",
															},
															{
																key: "Complete", name: t("action.complete"),
																handler: handleChnageStatus,
																isDelete: false,
																showInStatus: ["T"],
																tasksSkipped: true,
																otherDetail: "C",
															},
															{
																key: "Incomplete", name: t("action.incomplete"),
																handler: handleChnageStatus,
																isDelete: false,
																showInStatus: ["T"],
																otherDetail: "N",
															},
															{
																key: "Completed by Paper", name: t("action.completedByPaper"),
																handler: handleChnageStatus,
																isDelete: false,
																showInStatus: ["S"],
																otherDetail: "P",
															},
															{
																key: "Remove", name: t("action.remove"),
																handler: handleDeleteDialogOpen,
																isDelete: true,
																showInStatus: ["S"],
															},
														].filter((x) => {
															if (x.showInStatus === "Edit") return true;
															if (isReadOnly) return false;
															if (
																x.key === "Complete" &&
																x.showInStatus === data[index]["status"] &&
																x?.tasksSkipped === true
															) {
																return true;
															}
															if (
																x.showInStatus?.includes(data[index]["status"])
															)
																return true;
															return false;
														})}
													/>
												</AT.DotMenu>
											) : null}
										</AT.CellContainer>
									</AT.DataCell>
								))}
							</TableRow>
						))}
						{hasNextPage && (
							<TableRow ref={sentinelRef}>
								<TableCell
									colSpan={columns.length}
									style={{ padding: "12px 10px", textAlign: "center", border: 0 }}
								>
									{isFetchingNextPage ? <b>{t("message.loading")}</b> : " "}
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
				{!hasNextPage && data.length > 0 && (
					<div
						style={{ textAlign: "center", padding: "16px 10px" }}
						className="flex justify-center"
					>
						<span
							className="link-color ml-md cursor-pointer"
							onClick={() => ref?.current?.gotoTop?.()}
						>
							{t("action.goToTop")}
						</span>
					</div>
				)}
			</AutoFitContentInScreen>
		</>
	);
});

export default ServiceListTable;
